import { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    TextField,
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    Popper,
    ClickAwayListener,
    CircularProgress,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MULTI_SELECT_FIELD_NAMES } from '../pages/Products/EditItem/Constants';
import { sortByKey } from '../utils/helpers';

const filterInputStyle = {
    flex: "0 0 calc(20% - 10px)",
    margin: "8px 5px",
    width: "206px",
    "& .MuiOutlinedInput-root": {
        ".input-field": {
            padding: "7.5px 12px"
        },
        padding: 0,
        input: {
            paddingBottom: 0,
        },
    },
};
const modalStyle = {
    position: 'absolute',
    bgcolor: 'background.paper',
    border: '2px solid #ccc'
};

export default function AutocompleteWithModal({
    fieldName,
    label,
    optionsList,
    handleCloseModal,
    handleClear,
    optionLabelGetter = undefined,
    disabled = false,
    preSelectedOptions = [],
    setIsResetCalled,
    isResetCalled,
    showSelectAll = false,
    disabledOptions = [],
    onOpenModal = undefined,
    isOptionsLoading = false,
}) {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [filteredOpions, setFilteredOpions] = useState(optionsList);
    const [blurTriggered, setBlurTriggered] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingOpenAnchor, setPendingOpenAnchor] = useState(null);
    const [changeReason, setChangeReason] = useState("");
    const searchRef = useRef();

    const closeAllModals = () => {
        const allPoppers = document.querySelectorAll('[id$="-popper-unique-id"]');
        allPoppers.forEach(popper => {
            (popper as HTMLElement).style.display = 'none';
        });
        
        setAnchorEl(null);
        setPendingOpenAnchor(null);
    };

    const openModal = (target) => {
        closeAllModals();
        setAnchorEl(target);
        if (optionsList?.length > 0) {
            setTimeout(() => {
                (searchRef?.current as any)?.focus();
            }, 200);
        }
    };

    const handleOpenModal = (event) => {
        if (onOpenModal) {
            onOpenModal(fieldName);
            setPendingOpenAnchor(event.currentTarget);
            return;
        }

        openModal(event.currentTarget);
    };

    useEffect(() => {
        if (pendingOpenAnchor && !isOptionsLoading) {
            openModal(pendingOpenAnchor);
            setPendingOpenAnchor(null);
        }
    }, [isOptionsLoading, pendingOpenAnchor]);

    const handleCloseFuncion = () => {
        setAnchorEl(null);
        setPendingOpenAnchor(null);
    };

    const isPopperOpen = Boolean(anchorEl);
    const hasOptions = Boolean(optionsList?.length);

    const handleCheckboxChange = (option) => {
        setSelectedOptions((prev) =>
            prev.find(item => (typeof option === "string" ? item === option : item.id === option.id))
                ? prev.filter((item) => typeof option === "string" ? item !== option : item.id !== option.id)
                : [...prev, option]
        );
    };

    useEffect(() => {
        if (preSelectedOptions?.length > 0 && JSON.stringify(preSelectedOptions) !== JSON.stringify(selectedOptions)) {
            setSelectedOptions(preSelectedOptions);
        }
    }, [preSelectedOptions]);

    useEffect(() => {
        if (isResetCalled) {
            setSelectedOptions(preSelectedOptions);
            setIsResetCalled(false);
        }
    }, [isResetCalled]);

    useEffect(() => {
        setFilteredOpions(optionsList?.filter(Boolean));
    }, [optionsList, blurTriggered]);

    const getSortedValues = (arr, isDes: boolean = false) => {
        if (arr.length > 0) {
            if (typeof arr[0] === "string") {
                return arr?.sort((a, b) => isDes ? b.localeCompare(a) : a.localeCompare(b))?.map(item => optionLabelGetter ? optionLabelGetter(item) : item)?.join(', ');
            };
            const sortKey = [MULTI_SELECT_FIELD_NAMES.status, MULTI_SELECT_FIELD_NAMES.function].includes(fieldName) ? "description" : "name";
            return arr?.sort(sortByKey(sortKey))?.map(item => item?.name || item?.description || item).join(', ');
        }
        return '';
    };
    return (
        <>
            <Autocomplete
                key={fieldName}
                sx={filterInputStyle}
                className={`filter-autocomplete-dropdown multiselect-dropdown-modal ${selectedOptions?.length > 0 ? " filter-selected" : ""}`}
                multiple
                disabled={disabled}
                disableClearable={(fieldName === MULTI_SELECT_FIELD_NAMES.developmentType)}
                disableCloseOnSelect
                options={optionsList?.length > 0 ? optionsList : []}
                value={selectedOptions}
                open={false}
                loading={isOptionsLoading || Boolean(pendingOpenAnchor)}
                getOptionLabel={(option) => option?.name || option?.description || option}
                renderTags={() => null}
                onChange={(event, newValue, reason) => {
                    setSelectedOptions(newValue);
                    setChangeReason(reason);
                }}
                onFocus={() => setBlurTriggered(false)}
                onBlur={() => setBlurTriggered(true)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        className='multiselect-input'
                        label={label}
                        onClick={handleOpenModal}
                        InputProps={{
                            ...params.InputProps,
                            readOnly: true,
                            sx: { height: 56 },
                            endAdornment: (
                                <>
                                    {(isOptionsLoading || pendingOpenAnchor) && (
                                        <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                    )}
                                    {selectedOptions.length > 0 && (
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const isDisabledOptionSelected = selectedOptions?.some(option => disabledOptions?.includes(option?.id || option));
                                                const disabledOptionsState = optionsList?.filter(option => disabledOptions?.includes(option?.id || option));
                                                
                                                setSelectedOptions(isDisabledOptionSelected ? [...disabledOptionsState] : []);
                                                if (handleClear) {
                                                    setAnchorEl(null);
                                                    handleClear(fieldName);
                                                    closeAllModals();
                                                }
                                            }}
                                            size="small"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </>
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps,
                            value: getSortedValues([...selectedOptions], fieldName === MULTI_SELECT_FIELD_NAMES.slIntroDate),
                            title: getSortedValues([...selectedOptions], fieldName === MULTI_SELECT_FIELD_NAMES.slIntroDate),
                            readOnly: true,
                            'aria-readonly': true,
                        }}
                    />
                )}
            />

            <Popper id={`${fieldName}-popper-unique-id`} open={isPopperOpen} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300, width: anchorEl ? anchorEl.getBoundingClientRect().width : undefined, }}>
                <ClickAwayListener onClickAway={() => handleCloseFuncion()}>
                    <Box className="multiselect-modal-box" sx={modalStyle}>
                        {!isOptionsLoading && hasOptions && (
                            <Box className="input-container" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <TextField
                                    inputRef={searchRef}
                                    placeholder='Search here'
                                    className='search-input'
                                    onChange={(e) => setFilteredOpions(e.target.value?.trim() ? optionsList?.filter(item => (item?.name || item?.description || item)?.toLocaleLowerCase().includes(e.target.value?.trim()?.toLocaleLowerCase())) : optionsList)}
                                />
                            </Box>
                        )}
                        <Box className="multiselect-modal-options-list">
                            {isOptionsLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : !hasOptions ? (
                                <Box display="flex" justifyContent="center" alignItems="center" py={3} px={2}>
                                    <Typography variant="body2" sx={{ marginRight: "15px" }} color="text.secondary">
                                        No options
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                            {showSelectAll && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            className='check-box'
                                            checked={selectedOptions.length === optionsList?.length}
                                            onChange={(e) => {
                                                const isDisabledOptionSelected = selectedOptions?.some(option => disabledOptions?.includes(option?.id || option));
                                                const disabledOptionsState = optionsList?.filter(option => disabledOptions?.includes(option?.id || option));
                                                const enabledOptionsState = optionsList?.filter(option => !disabledOptions?.includes(option?.id || option));
                                                const isChecked = e.target.checked;
                                                const getCorrectOptions = () => {
                                                    if(isChecked) {
                                                        return isDisabledOptionSelected ? optionsList : enabledOptionsState;
                                                    } else {
                                                        return isDisabledOptionSelected ? [...disabledOptionsState] : [];
                                                    }
                                                };
                                                setSelectedOptions(getCorrectOptions());
                                                if (handleCloseModal) {
                                                    handleCloseModal(fieldName, getCorrectOptions(), changeReason);
                                                }
                                            }}
                                        />}
                                        label="Select All"
                                />
                            )}
                            {filteredOpions?.map((option) => (
                                <FormControlLabel
                                key={typeof option === "string" ? option : option.id}
                                control={
                                        <Checkbox
                                            className='check-box'
                                            checked={!!selectedOptions.find(item => typeof option === "string" ? item === option : item.id === option.id)}
                                            onChange={() => {
                                                handleCheckboxChange(option);
                                                if (handleCloseModal) {
                                                    const newSelectedOptions = selectedOptions.find(item => (typeof option === "string" ? item === option : item.id === option.id))
                                                        ? selectedOptions.filter((item) => typeof option === "string" ? item !== option : item.id !== option.id)
                                                        : [...selectedOptions, option];
                                                        handleCloseModal(fieldName, newSelectedOptions, changeReason);
                                                    }
                                                }}
                                            disabled={disabledOptions?.includes(option?.id || option?.name || option?.description || option)}
                                        />
                                    }
                                    label={optionLabelGetter ? optionLabelGetter(option) : option?.name || option?.description || option}
                                />
                            ))}
                                </>
                            )}
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
